import * as React from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const PULL_THRESHOLD = 80;

export interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
  className?: string;
}

export function PullToRefresh({ onRefresh, children, className }: PullToRefreshProps) {
  const [pullY, setPullY] = React.useState(0);
  const [refreshing, setRefreshing] = React.useState(false);
  const startYRef = React.useRef<number | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  function handleTouchStart(e: React.TouchEvent) {
    const scrollTop = containerRef.current?.scrollTop ?? 0;
    if (scrollTop > 0 || refreshing) return;
    startYRef.current = e.touches[0]?.clientY ?? 0;
  }

  function handleTouchMove(e: React.TouchEvent) {
    if (startYRef.current === null || refreshing) return;
    const scrollTop = containerRef.current?.scrollTop ?? 0;
    if (scrollTop > 0) {
      startYRef.current = null;
      return;
    }
    const dy = (e.touches[0]?.clientY ?? 0) - startYRef.current;
    if (dy > 0) {
      setPullY(Math.min(dy, PULL_THRESHOLD * 1.5));
    }
  }

  async function handleTouchEnd() {
    if (startYRef.current === null) return;
    startYRef.current = null;
    if (pullY >= PULL_THRESHOLD && !refreshing) {
      setPullY(PULL_THRESHOLD);
      setRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setRefreshing(false);
        setPullY(0);
      }
    } else {
      setPullY(0);
    }
  }

  const indicatorVisible = pullY > 8 || refreshing;
  const progress = Math.min(pullY / PULL_THRESHOLD, 1);

  return (
    <div
      ref={containerRef}
      className={cn("relative overflow-y-auto", className)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator */}
      {indicatorVisible && (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 flex justify-center"
          style={{ height: Math.max(pullY, refreshing ? PULL_THRESHOLD : 0) }}
          data-testid="pull-indicator"
        >
          <div className="flex items-center justify-center">
            <Loader2
              className={cn(
                "size-5 text-ink-400 transition-transform",
                refreshing && "animate-spin",
              )}
              style={!refreshing ? { transform: `rotate(${progress * 360}deg)` } : undefined}
            />
          </div>
        </div>
      )}
      {/* Content offset while pulling */}
      <div
        style={{
          transform: `translateY(${Math.max(pullY, refreshing ? PULL_THRESHOLD : 0)}px)`,
          transition: pullY === 0 ? "transform 0.2s ease" : undefined,
        }}
      >
        {children}
      </div>
    </div>
  );
}
